import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Save,
  Loader2,
  Upload,
  ArrowUp,
  ArrowDown,
  Eye,
  EyeOff,
} from 'lucide-react';
import { client } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const emptySlide = () => ({
  eyebrow: '',
  title: '',
  subtitle: '',
  cta: 'Lihat Produk',
  cta_link: '/products',
  image: '',
  order: 0,
  active: true,
  show_overlay: true,
});

// Convert /api/images/... relative URL into absolute so <img> loads
const absUrl = (u) => (u && u.startsWith('/api/') ? `${API}${u}` : u);

export default function AdminBanners() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRefs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get('/api/admin/hero-slides');
      setSlides(data);
    } catch (err) {
      toast.error('Gagal memuat slide');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const patch = (id, k, v) =>
    setSlides((s) => s.map((x) => (x.id === id ? { ...x, [k]: v } : x)));

  const saveSlide = async (slide) => {
    setSavingId(slide.id);
    try {
      const { id, ...body } = slide;
      await client.put(`/api/admin/hero-slides/${id}`, body);
      toast.success('Slide tersimpan');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSavingId(null);
    }
  };

  const addSlide = async () => {
    try {
      const body = {
        ...emptySlide(),
        title: 'Slide baru',
        order: slides.length + 1,
      };
      const { data } = await client.post('/api/admin/hero-slides', body);
      setSlides((s) => [...s, data]);
      toast.success('Slide ditambahkan');
    } catch (err) {
      toast.error('Gagal menambah slide');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Hapus slide ini?')) return;
    try {
      await client.delete(`/api/admin/hero-slides/${id}`);
      setSlides((s) => s.filter((x) => x.id !== id));
      toast.success('Slide dihapus');
    } catch (err) {
      toast.error('Gagal menghapus');
    }
  };

  const move = async (id, dir) => {
    const idx = slides.findIndex((s) => s.id === id);
    const target = idx + dir;
    if (target < 0 || target >= slides.length) return;
    const swapped = [...slides];
    [swapped[idx], swapped[target]] = [swapped[target], swapped[idx]];
    // reassign order numbers
    swapped.forEach((s, i) => (s.order = i + 1));
    setSlides(swapped);
    // persist both changed rows
    try {
      await Promise.all(
        [swapped[idx], swapped[target]].map(({ id: sid, ...body }) =>
          client.put(`/api/admin/hero-slides/${sid}`, body)
        )
      );
    } catch (err) {
      toast.error('Gagal mengurutkan');
      load();
    }
  };

  const upload = async (id, file) => {
    if (!file) return;
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'banners');
      const { data } = await client.post('/api/admin/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      patch(id, 'image', data.url);
      toast.success('Gambar terunggah. Klik Simpan untuk menerapkan.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload gagal');
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div data-testid="admin-banners-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-xs tracking-[0.25em] text-px-red font-semibold mb-3">
            BANNER HERO
          </p>
          <h1 className="font-display text-4xl font-bold text-neutral-900 mb-2">
            Banner Hero
          </h1>
          <p className="text-neutral-600">
            Kelola slide di halaman beranda. Urutkan dengan tombol panah.
          </p>
        </div>
        <button
          onClick={addSlide}
          data-testid="add-slide-btn"
          className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-px-red text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
        >
          <Plus className="w-4 h-4" /> Tambah Slide
        </button>
      </div>

      {loading ? (
        <div className="text-neutral-500 text-sm">Memuat slide...</div>
      ) : slides.length === 0 ? (
        <div className="bg-white rounded-2xl border border-neutral-200 p-8 text-center text-sm text-neutral-500">
          Belum ada slide. Klik "Tambah Slide" untuk memulai.
        </div>
      ) : (
        <div className="space-y-5">
          {slides.map((s, i) => (
            <div
              key={s.id}
              data-testid={`slide-${s.id}`}
              className="bg-white rounded-2xl border border-neutral-200 p-6 grid md:grid-cols-[220px_1fr] gap-6"
            >
              <div>
                <div className="aspect-video rounded-xl bg-neutral-100 overflow-hidden border border-neutral-200">
                  {s.image ? (
                    <img
                      src={absUrl(s.image)}
                      alt={s.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-neutral-400">
                      Belum ada gambar
                    </div>
                  )}
                </div>
                <input
                  ref={(el) => (fileRefs.current[s.id] = el)}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => upload(s.id, e.target.files?.[0])}
                />
                <button
                  onClick={() => fileRefs.current[s.id]?.click()}
                  disabled={uploadingId === s.id}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-neutral-300 hover:border-neutral-900 rounded-full py-2 text-xs font-semibold disabled:opacity-60"
                  data-testid={`upload-btn-${s.id}`}
                >
                  {uploadingId === s.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5" />
                  )}
                  Ganti Gambar
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Eyebrow (label kecil di atas judul)">
                  <input
                    value={s.eyebrow || ''}
                    onChange={(e) => patch(s.id, 'eyebrow', e.target.value)}
                    className={inputCls}
                  />
                </Field>
                <Field label="Urutan">
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-sm font-semibold">#{i + 1}</span>
                    <button
                      type="button"
                      onClick={() => move(s.id, -1)}
                      disabled={i === 0}
                      className="p-1.5 rounded hover:bg-neutral-100 disabled:opacity-30"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(s.id, 1)}
                      disabled={i === slides.length - 1}
                      className="p-1.5 rounded hover:bg-neutral-100 disabled:opacity-30"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                  </div>
                </Field>
                <Field label="Judul" full>
                  <input
                    value={s.title || ''}
                    onChange={(e) => patch(s.id, 'title', e.target.value)}
                    className={inputCls}
                  />
                </Field>
                <Field label="Subtitle" full>
                  <textarea
                    rows={2}
                    value={s.subtitle || ''}
                    onChange={(e) => patch(s.id, 'subtitle', e.target.value)}
                    className={`${inputCls} resize-none`}
                  />
                </Field>
                <Field label="Teks Tombol CTA">
                  <input
                    value={s.cta || ''}
                    onChange={(e) => patch(s.id, 'cta', e.target.value)}
                    className={inputCls}
                  />
                </Field>
                <Field label="Link Tombol (URL)">
                  <input
                    value={s.cta_link || ''}
                    onChange={(e) => patch(s.id, 'cta_link', e.target.value)}
                    className={inputCls}
                  />
                </Field>

                <div className="md:col-span-2 flex items-center justify-between pt-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => patch(s.id, 'active', !s.active)}
                      className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
                        s.active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-neutral-200 text-neutral-600'
                      }`}
                    >
                      {s.active ? (
                        <Eye className="w-3.5 h-3.5" />
                      ) : (
                        <EyeOff className="w-3.5 h-3.5" />
                      )}
                      {s.active ? 'Aktif' : 'Nonaktif'}
                    </button>
                    <label
                      className="inline-flex items-center gap-1.5 text-xs text-neutral-700 cursor-pointer bg-neutral-100 px-3 py-1.5 rounded-full"
                      data-testid={`overlay-toggle-${s.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={s.show_overlay !== false}
                        onChange={(e) =>
                          patch(s.id, 'show_overlay', e.target.checked)
                        }
                        className="rounded"
                      />
                      Tampilkan teks overlay
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => remove(s.id)}
                      className="inline-flex items-center gap-1.5 text-red-600 hover:bg-red-50 text-xs font-semibold px-3 py-2 rounded-full transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Hapus
                    </button>
                    <button
                      type="button"
                      onClick={() => saveSlide(s)}
                      disabled={savingId === s.id}
                      data-testid={`save-slide-${s.id}`}
                      className="inline-flex items-center gap-1.5 bg-px-red hover:bg-red-700 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors"
                    >
                      {savingId === s.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Save className="w-3.5 h-3.5" />
                      )}
                      Simpan
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const inputCls =
  'mt-2 w-full bg-neutral-50 border border-neutral-200 focus:border-neutral-900 outline-none rounded-lg px-3 py-2 text-sm';

const Field = ({ label, children, full }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="text-[11px] uppercase tracking-wider text-neutral-600 font-semibold">
      {label}
    </label>
    {children}
  </div>
);
